import React, { useEffect, useRef, useState } from 'react'

const PROCS = {
  agedRemoval: { id: 106, name: 'kognative_AGED_RECORD_REMOVAL' }
}

function LoadingStatValue() {
  return <span style={styles.loadingStatValue} aria-label="Loading stat" />
}

function AgedRecordRemoval({ orgData }) {
  const [activeTab, setActiveTab] = useState('run')
  const [years, setYears] = useState('5')
  const [pool, setPool] = useState([])
  const [report, setReport] = useState([])
  const [loading, setLoading] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [finding, setFinding] = useState(false)
  const [step, setStep] = useState(null) // null | 'review' | 'committed'
  const [message, setMessage] = useState(null)
  const [confirmCommit, setConfirmCommit] = useState(false)
  const [hasReviewed, setHasReviewed] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [reactivating, setReactivating] = useState(null)
  const [ageStats, setAgeStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [segmentMinYears, setSegmentMinYears] = useState('3')
  const [segmentMaxYears, setSegmentMaxYears] = useState('5')
  const [segmentLoading, setSegmentLoading] = useState(false)
  const [segmentResult, setSegmentResult] = useState(null)
  const setupDoneRef = useRef(false)

  const headers = {
    'x-tessitura-auth': orgData.organizations.tessitura_auth_string,
    'x-tessitura-url': orgData.organizations.tessitura_base_url,
    'Content-Type': 'application/json'
  }

  const callProc = async (proc, parameterValues = [], includeId = true) => {
    const body = {
      ...(includeId ? { ProcedureId: proc.id } : {}),
      ProcedureName: proc.name
    }
    if (parameterValues.length > 0) {
      body.ParameterValues = parameterValues
    }

    const response = await fetch(`/api/tessitura?endpoint=Custom/Execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const callAgedProc = async (parameterValues = []) => {
    const result = await callProc(PROCS.agedRemoval, parameterValues, true)
    if (!result.ok && result.status === 400) {
      return callProc(PROCS.agedRemoval, parameterValues, false)
    }
    return result
  }

  const ensureSetup = async () => {
    if (setupDoneRef.current) return true

    const result = await callAgedProc([
      { Name: '@mode', Value: 'SETUP' }
    ])

    if (result.ok) {
      setupDoneRef.current = true
      return true
    }

    const probe = await callAgedProc([
      { Name: '@mode', Value: 'DATA_AGE' }
    ])

    if (probe.ok) {
      setupDoneRef.current = true
      return true
    }

    setMessage({ type: 'error', text: describeError('Could not initialize aged record inactivation tables.', probe.ok ? result : probe) })
    return false
  }

  const describeError = (fallback, result) => {
    if (!result?.text) return fallback
    try {
      const parsed = JSON.parse(result.text)
      const detail = parsed?.Message || parsed?.message || parsed?.error || parsed?.ExceptionMessage
      return detail ? `${fallback} ${detail}` : fallback
    } catch {
      return `${fallback} ${result.text}`.trim()
    }
  }

  const parseRows = (text) => {
    try {
      const data = JSON.parse(text)
      return Array.isArray(data) ? data : []
    } catch {
      return []
    }
  }

  const encodeSql = (sql) => {
    return btoa(unescape(encodeURIComponent(sql)))
  }

  const listApiBase = () => {
    return (orgData?.organizations?.tessitura_base_url || '')
      .replace(/\/TessituraService\/?$/i, '/Tessitura/api/')
  }

  const fetchAgeStats = async () => {
    setStatsLoading(true)
    try {
      const ready = await ensureSetup()
      if (!ready) {
        setStatsLoading(false)
        return
      }

      const result = await callAgedProc([
        { Name: '@mode', Value: 'DATA_AGE' }
      ])

      if (result.ok) {
        const row = parseRows(result.text)[0] || {}
        setAgeStats({
          activeCustomerCount: Number(row.active_customer_count || 0),
          avgDataAgeYears: row.avg_data_age_years ?? null,
          oldestDataAgeYears: row.oldest_data_age_years ?? null,
          neverActiveCount: Number(row.never_active_count || 0)
        })
      }
    } catch (err) {
      console.error('Aged data stats failed:', err)
    }
    setStatsLoading(false)
  }

  useEffect(() => {
    fetchAgeStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCreateSegment = async () => {
    const minYears = parseInt(segmentMinYears, 10)
    const maxYears = parseInt(segmentMaxYears, 10)
    if (isNaN(minYears) || isNaN(maxYears) || minYears < 0 || maxYears <= minYears) {
      setSegmentResult({ type: 'error', text: 'Enter a valid year range where the second value is greater than the first.' })
      return
    }

    setSegmentLoading(true)
    setSegmentResult(null)
    setMessage(null)

    try {
      const ready = await ensureSetup()
      if (!ready) {
        setSegmentLoading(false)
        return
      }

      const sqlResult = await callAgedProc([
        { Name: '@mode', Value: 'LIST_SQL' },
        { Name: '@min_years', Value: String(minYears) },
        { Name: '@max_years', Value: String(maxYears) }
      ])

      if (!sqlResult.ok) {
        setSegmentResult({ type: 'error', text: describeError('Could not build the aged range SQL.', sqlResult) })
        setSegmentLoading(false)
        return
      }

      const sqlRow = parseRows(sqlResult.text)[0] || {}
      const listSql = sqlRow.list_sql
      const description = (sqlRow.description || `Aged ${minYears}-${maxYears} Years`).slice(0, 30)
      if (!listSql) {
        setSegmentResult({ type: 'error', text: 'The aged range SQL did not come back from Tessitura.' })
        setSegmentLoading(false)
        return
      }

      const createResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent('Custom/Execute')}`, {
        method: 'POST',
        headers: {
          'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
          'x-tessitura-url': listApiBase(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProcedureName: 'kognative_CREATE_TESSBUDDY_LIST',
          ParameterValues: [
            { Name: '@description', Value: description },
            { Name: '@list_sql_b64', Value: encodeSql(listSql) },
            { Name: '@category_id', Value: '31' }
          ]
        })
      })

      const createText = await createResp.text()
      if (!createResp.ok) {
        setSegmentResult({ type: 'error', text: createText.slice(0, 300) || `Tessitura returned ${createResp.status}.` })
        setSegmentLoading(false)
        return
      }

      let listId = null
      try {
        const created = JSON.parse(createText)
        const row = Array.isArray(created) ? created[0] : created
        listId = row?.Id ?? row?.id ?? row?.list_id ?? null
      } catch {}

      let count = null
      if (listId) {
        try {
          const genResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent(`Reporting/Lists/${listId}/Generate`)}`, {
            method: 'POST',
            headers: {
              'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
              'x-tessitura-url': listApiBase(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
          })

          if (genResp.ok) {
            const summaryResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent(`Reporting/Lists/Summary/${listId}`)}`, {
              method: 'GET',
              headers: {
                'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
                'x-tessitura-url': listApiBase(),
                'Content-Type': 'application/json'
              }
            })
            if (summaryResp.ok) {
              const summary = await summaryResp.json()
              count = summary?.ConstituentCount ?? summary?.constituentCount ?? null
            }
          }
        } catch {
          console.warn('Aged range list created but generation/count failed.')
        }
      }

      setSegmentResult({
        type: 'success',
        text: `Created List #${listId || '?'}${count != null ? ` with ${Number(count).toLocaleString()} constituents` : ''}.`
      })
    } catch (err) {
      setSegmentResult({ type: 'error', text: 'Error creating aged range segment: ' + err.message })
    }

    setSegmentLoading(false)
  }

  const handleFind = async () => {
    const n = parseInt(years, 10)
    if (isNaN(n) || n < 1) {
      setMessage({ type: 'error', text: 'Please enter a valid number of years (1 or greater).' })
      return
    }

    setLoading(true)
    setFinding(true)
    setMessage(null)
    setPool([])
    setHasReviewed(false)
    setStep(null)

    try {
      const ready = await ensureSetup()
      if (!ready) {
        setLoading(false)
        setFinding(false)
        return
      }

      const result = await callAgedProc([
        { Name: '@mode', Value: 'FIND' },
        { Name: '@years', Value: String(n) }
      ])

      if (!result.ok) {
        setMessage({ type: 'error', text: describeError('Failed to find aged records.', result) })
        setLoading(false)
        setFinding(false)
        return
      }

      const rows = parseRows(result.text)
      setPool(rows)
      setStep('review')
      if (rows.length === 0) {
        setMessage({ type: 'success', text: 'No aged records found matching the criteria.' })
      } else {
        setMessage({ type: 'success', text: `Found ${rows.length} aged record${rows.length === 1 ? '' : 's'}. Review below, then commit to inactivate.` })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + err.message })
    }

    setLoading(false)
    setFinding(false)
  }

  const handleRemove = async (customer_no) => {
    setRemoving(customer_no)
    try {
      const result = await callAgedProc([
        { Name: '@mode', Value: 'MODIFY' },
        { Name: '@customer_no', Value: String(customer_no) },
        { Name: '@action', Value: 'REMOVE' }
      ])

      if (result.ok) {
        const rows = parseRows(result.text)
        setPool(rows)
        setHasReviewed(false)
        if (rows.length === 0) {
          setMessage({ type: 'success', text: 'All records have been removed from the inactivation queue.' })
        }
      } else {
        setMessage({ type: 'error', text: describeError(`Failed to remove customer ${customer_no}.`, result) })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + err.message })
    }
    setRemoving(null)
  }

  const handleCommit = async () => {
    setLoading(true)
    setCommitting(true)
    setConfirmCommit(false)
    setMessage(null)

    try {
      const ready = await ensureSetup()
      if (!ready) {
        setLoading(false)
        setCommitting(false)
        return
      }

      const result = await callAgedProc([
        { Name: '@mode', Value: 'INACTIVATE' }
      ])

      if (!result.ok) {
        setMessage({ type: 'error', text: describeError('Failed to inactivate records.', result) })
        setLoading(false)
        setCommitting(false)
        return
      }

      let count = 0
      try {
        const data = JSON.parse(result.text)
        const row = Array.isArray(data) ? data[0] : data
        count = row?.records_inactivated ?? 0
      } catch {}

      setPool([])
      setStep('committed')
      setHasReviewed(false)
      setActiveTab('recent')
      await loadReport()
      setMessage({ type: 'success', text: `Successfully inactivated ${count} record${count === 1 ? '' : 's'}. See "Recent Inactivations" tab to undo or reactivate individuals.` })
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + err.message })
    }

    setLoading(false)
    setCommitting(false)
  }

  const loadReport = async () => {
    setLoading(true)
    setMessage(null)
    try {
      const ready = await ensureSetup()
      if (!ready) {
        setReport([])
        setLoading(false)
        return
      }

      const result = await callAgedProc([
        { Name: '@mode', Value: 'REPORT' }
      ])
      if (result.ok) {
        setReport(parseRows(result.text))
      } else {
        // REPORT raises an error when empty - that's fine
        setReport([])
      }
    } catch (err) {
      setReport([])
    }
    setLoading(false)
  }

  const handleReactivate = async (customer_no) => {
    setReactivating(customer_no)
    try {
      const result = await callAgedProc([
        { Name: '@mode', Value: 'REACTIVATE' },
        { Name: '@customer_no', Value: String(customer_no) }
      ])
      if (result.ok) {
        setReport(prev => prev.map(r =>
          r.customer_no === customer_no
            ? {
                ...r,
                status: 'REACTIVATED',
                reactivated_dt: new Date().toISOString(),
                reactivated_by: r.reactivated_by || 'Current user'
              }
            : r
        ))
        setMessage({ type: 'success', text: `Reactivated customer ${customer_no}.` })
      } else {
        setMessage({ type: 'error', text: describeError(`Failed to reactivate customer ${customer_no}.`, result) })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + err.message })
    }
    setReactivating(null)
  }

  const handleUndoAll = async () => {
    setLoading(true)
    setUndoing(true)
    setMessage(null)
    try {
      const result = await callAgedProc([
        { Name: '@mode', Value: 'UNDO' }
      ])
      if (!result.ok) {
        setMessage({ type: 'error', text: describeError('Failed to undo. Nothing on record, or an error occurred.', result) })
        setLoading(false)
        setUndoing(false)
        return
      }
      let count = 0
      try {
        const data = JSON.parse(result.text)
        const row = Array.isArray(data) ? data[0] : data
        count = row?.records_reverted ?? 0
      } catch {}
      setReport([])
      await loadReport()
      setMessage({ type: 'success', text: `Reverted ${count} record${count === 1 ? '' : 's'}.` })
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + err.message })
    }
    setLoading(false)
    setUndoing(false)
  }

  const switchTab = (tab) => {
    setActiveTab(tab)
    setMessage(null)
    if (tab === 'recent') {
      loadReport()
    }
  }

  const messageStyle = (type) => {
    if (type === 'error') return { ...styles.message, backgroundColor: '#fff5f5', borderColor: '#feb2b2', color: '#c53030' }
    return { ...styles.message, backgroundColor: '#f0fff4', borderColor: '#9ae6b4', color: '#2f855a' }
  }

  const formatDate = (d) => {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString()
    } catch {
      return d
    }
  }

  const customerUrl = (customerNo) => {
    const baseUrl = orgData?.organizations?.tessitura_base_url || ''
    const appBase = baseUrl
      .replace(/\/$/, '')
      .replace(/\/api$/i, '')
    return `${appBase}/#/crm/constituents/${customerNo}/dashboard`
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes crmbuddy-spin { to { transform: rotate(360deg); } }
        @keyframes tessBuddyStatPulse {
          0% { opacity: 0.45; transform: scaleX(0.92); }
          50% { opacity: 1; transform: scaleX(1); }
          100% { opacity: 0.45; transform: scaleX(0.92); }
        }
      `}</style>
      <h1 style={styles.title}>Aged Record Inactivation</h1>
      <p style={styles.subtitle}>
        Find and inactivate constituents who have had no activity in the last N years.
      </p>

      <div style={styles.tabs}>
        <button
          style={activeTab === 'run' ? styles.tabActive : styles.tab}
          onClick={() => switchTab('run')}
        >
          Run Removal
        </button>
        <button
          style={activeTab === 'recent' ? styles.tabActive : styles.tab}
          onClick={() => switchTab('recent')}
        >
          Recent Inactivations
        </button>
      </div>

      {message && (
        <div style={messageStyle(message.type)}>{message.text}</div>
      )}

      {activeTab === 'run' && (
        <>
          <div style={styles.statsRow}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Average Data Age</div>
              <div style={styles.statValue}>
                {statsLoading && !ageStats ? <LoadingStatValue /> : ageStats?.avgDataAgeYears ?? '--'}
              </div>
              <div style={styles.statHint}>
                Years since last activity across {ageStats?.activeCustomerCount?.toLocaleString?.() || 'active'} active records
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statLabel}>Oldest Active Record</div>
              <div style={styles.statValue}>
                {statsLoading && !ageStats ? <LoadingStatValue /> : ageStats?.oldestDataAgeYears ?? '--'}
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statLabel}>Aged Range Segment</div>
              <div style={styles.rangeRow}>
                <input
                  type="number"
                  min="0"
                  value={segmentMinYears}
                  onChange={e => { setSegmentMinYears(e.target.value); setSegmentResult(null) }}
                  style={styles.rangeInput}
                  disabled={segmentLoading}
                  aria-label="Minimum years inactive"
                />
                <span style={styles.rangeText}>to</span>
                <input
                  type="number"
                  min="1"
                  value={segmentMaxYears}
                  onChange={e => { setSegmentMaxYears(e.target.value); setSegmentResult(null) }}
                  style={styles.rangeInput}
                  disabled={segmentLoading}
                  aria-label="Maximum years inactive"
                />
                <span style={styles.rangeText}>years</span>
              </div>
              <button
                style={{ ...styles.button, padding: '10px', marginTop: '10px', opacity: segmentLoading ? 0.55 : 1, cursor: segmentLoading ? 'not-allowed' : 'pointer' }}
                onClick={handleCreateSegment}
                disabled={segmentLoading}
              >
                {segmentLoading ? 'Creating...' : 'Add to List Manager'}
              </button>
              {segmentResult && (
                <div style={{
                  ...styles.segmentResult,
                  color: segmentResult.type === 'error' ? '#dc2626' : '#16a34a'
                }}>
                  {segmentResult.text}
                </div>
              )}
            </div>
          </div>

          <div style={styles.actionRow}>
            <div style={styles.actionCard}>
              <div>
                <div style={styles.actionTitle}>Step 1 — Find Aged Records</div>
                <div style={styles.actionDesc}>
                  Enter the inactivity threshold in years. Active customers whose last activity is older than this (or never) will be listed below for review.
                </div>
                <div style={styles.inputRow}>
                  <label style={styles.inputLabel}>Years of inactivity:</label>
                  <input
                    type="number"
                    min="1"
                    value={years}
                    onChange={e => setYears(e.target.value)}
                    style={styles.input}
                    disabled={loading}
                  />
                </div>
              </div>
              <button
                style={{ ...styles.button, opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                onClick={handleFind}
                disabled={loading}
              >
                {loading && step !== 'review' ? 'Searching...' : 'Find Aged Records'}
              </button>
            </div>

            <div style={styles.stepArrow}>→</div>

            <div style={styles.actionCard}>
              <div>
                <div style={styles.actionTitle}>Step 2 — Commit Inactivation</div>
                <div style={styles.actionDesc}>
                  Review the list below. Remove any records you want to keep, then confirm you've reviewed and commit to inactivate the rest.
                </div>
              </div>
              <button
                style={{
                  ...styles.commitButton,
                  opacity: pool.length === 0 || !hasReviewed || loading ? 0.5 : 1,
                  cursor: pool.length === 0 || !hasReviewed || loading ? 'not-allowed' : 'pointer'
                }}
                onClick={() => setConfirmCommit(true)}
                disabled={pool.length === 0 || !hasReviewed || loading}
              >
                Inactivate {pool.length > 0 ? `${pool.length} Record${pool.length === 1 ? '' : 's'}` : 'Records'}
              </button>
              {pool.length > 0 && !hasReviewed && (
                <div style={styles.reviewNote}>Click "I've Reviewed These Records" below to enable.</div>
              )}
            </div>
          </div>

          {pool.length > 0 && (
            <div style={styles.tableSection}>
              <div style={styles.tableHeader}>
                <h3 style={styles.tableTitle}>
                  Aged Records — {pool.length} pending inactivation
                </h3>
                {hasReviewed ? (
                  <span style={styles.reviewedBadge}>✓ Reviewed</span>
                ) : (
                  <button style={styles.reviewedButton} onClick={() => setHasReviewed(true)}>
                    I've Reviewed These Records
                  </button>
                )}
              </div>
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Customer #</th>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Created</th>
                      <th style={styles.th}>Last Activity</th>
                      <th style={styles.th}>Last Contribution</th>
                      <th style={styles.th}>Last Ticket</th>
                      <th style={styles.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pool.map((row, index) => {
                      const url = customerUrl(row.customer_no)
                      const isRemoving = removing === row.customer_no
                      return (
                        <tr key={row.customer_no} style={index % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                          <td style={styles.td}>
                            <a href={url} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>
                              {row.customer_no}
                            </a>
                          </td>
                          <td style={styles.td}>
                            {[row.fname, row.mname, row.lname].filter(Boolean).join(' ')}
                          </td>
                          <td style={styles.td}>{formatDate(row.create_dt)}</td>
                          <td style={styles.td}>{formatDate(row.last_activity_dt)}</td>
                          <td style={styles.td}>{formatDate(row.last_gift_dt)}</td>
                          <td style={styles.td}>{formatDate(row.last_ticket_dt)}</td>
                          <td style={styles.td}>
                            <button
                              style={{
                                ...styles.cancelPairButton,
                                opacity: isRemoving ? 0.5 : 1,
                                cursor: isRemoving ? 'not-allowed' : 'pointer'
                              }}
                              onClick={() => handleRemove(row.customer_no)}
                              disabled={isRemoving}
                              title="Remove from inactivation queue"
                            >
                              {isRemoving ? '...' : '✕ Unschedule'}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'recent' && (
        <>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>
              Recent Inactivations {report.length > 0 && `— ${report.length} record${report.length === 1 ? '' : 's'}`}
            </h3>
            {report.some(r => r.inactivated_dt && !r.reactivated_dt) && (
              <button
                style={{ ...styles.cancelPairButton, padding: '8px 16px', fontSize: '13px' }}
                onClick={handleUndoAll}
                disabled={loading}
                title="Revert every record from the most recent inactivation run"
              >
                ↻ Undo Entire Batch
              </button>
            )}
          </div>

          {report.length === 0 && !loading && (
            <div style={styles.emptyState}>
              No recent inactivations on record.
            </div>
          )}

          {report.length > 0 && (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Customer #</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Last Activity</th>
                    <th style={styles.th}>Last Contribution</th>
                    <th style={styles.th}>Last Ticket</th>
                    <th style={styles.th}>Inactivated</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {report.map((row, index) => {
                    const url = customerUrl(row.customer_no)
                    const isReactivating = reactivating === row.customer_no
                    const isReactivated = Boolean(row.reactivated_dt) || String(row.status || '').toUpperCase() === 'REACTIVATED'
                    return (
                      <tr key={row.log_id || `${row.customer_no}-${row.inactivated_dt || index}`} style={index % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.badge,
                            backgroundColor: isReactivated ? '#d97706' : '#718096'
                          }}>
                            {isReactivated ? 'Reactivated' : 'Inactive'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <a href={url} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>
                            {row.customer_no}
                          </a>
                        </td>
                        <td style={styles.td}>
                          {[row.fname, row.mname, row.lname].filter(Boolean).join(' ')}
                        </td>
                        <td style={styles.td}>{formatDate(row.last_activity_dt)}</td>
                        <td style={styles.td}>{formatDate(row.last_gift_dt)}</td>
                        <td style={styles.td}>{formatDate(row.last_ticket_dt)}</td>
                        <td style={styles.td}>{formatDate(row.inactivated_dt)}</td>
                        <td style={styles.td}>
                          {!isReactivated && (
                            <button
                              style={{
                                ...styles.mergeButton,
                                opacity: isReactivating ? 0.5 : 1,
                                cursor: isReactivating ? 'not-allowed' : 'pointer'
                              }}
                              onClick={() => handleReactivate(row.customer_no)}
                              disabled={isReactivating}
                              title="Reactivate this customer"
                            >
                              {isReactivating ? '...' : '↻ Reactivate'}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {(committing || undoing || finding) && (
        <div style={styles.overlay}>
          <div style={styles.loadingDialog}>
            <div style={styles.spinner} />
            <div style={styles.loadingTitle}>
              {committing
                ? 'Inactivating records...'
                : undoing
                  ? 'Reverting records...'
                  : 'Finding aged records...'}
            </div>
            <div style={styles.loadingText}>This may take a moment. Please don't close the page.</div>
          </div>
        </div>
      )}

      {confirmCommit && (
        <div style={styles.overlay} onClick={() => setConfirmCommit(false)}>
          <div style={styles.dialog} onClick={e => e.stopPropagation()}>
            <div style={styles.dialogTitle}>Confirm Inactivation</div>
            <div style={styles.dialogText}>
              This will set <strong>{pool.length}</strong> customer record{pool.length === 1 ? '' : 's'} to inactive in Tessitura. You can undo this from the "Recent Inactivations" tab until the next run.
            </div>
            <div style={styles.dialogButtons}>
              <button style={styles.cancelButton} onClick={() => setConfirmCommit(false)}>Cancel</button>
              <button style={styles.commitButton} onClick={handleCommit}>Inactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { padding: '40px' },
  title: { fontSize: '22px', fontWeight: '700', color: '#0c1a33', marginBottom: '8px', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' },
  subtitle: { fontSize: '14px', color: '#4b5563', marginBottom: '24px', fontFamily: "'Inter', sans-serif" },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid rgba(29,111,219,0.15)' },
  tab: { padding: '10px 20px', backgroundColor: 'transparent', color: '#6b7280', border: 'none', borderBottom: '2px solid transparent', fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  tabActive: { padding: '10px 20px', backgroundColor: 'transparent', color: '#1d6fdb', border: 'none', borderBottom: '2px solid #1d6fdb', fontSize: '14px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', minHeight: '150px', boxSizing: 'border-box' },
  statLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '8px', fontFamily: "'Inter', sans-serif" },
  statValue: { fontSize: '34px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1.1 },
  loadingStatValue: { display: 'inline-block', width: '72px', height: '34px', borderRadius: '8px', background: 'linear-gradient(135deg, rgba(29,111,219,0.12), rgba(56,189,248,0.28))', transformOrigin: 'left center', animation: 'tessBuddyStatPulse 1.1s ease-in-out infinite' },
  statHint: { fontSize: '12px', color: '#6b7280', marginTop: '10px', lineHeight: '1.45', fontFamily: "'Inter', sans-serif" },
  rangeRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  rangeInput: { width: '64px', padding: '8px 10px', border: '1px solid rgba(29,111,219,0.22)', borderRadius: '6px', fontSize: '14px', fontFamily: "'Inter', sans-serif", color: '#0c1a33', outline: 'none' },
  rangeText: { fontSize: '13px', color: '#4b5563', fontFamily: "'Inter', sans-serif" },
  segmentResult: { marginTop: '8px', fontSize: '12px', fontWeight: '600', lineHeight: '1.35', fontFamily: "'Inter', sans-serif" },
  actionRow: { display: 'flex', alignItems: 'stretch', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  actionCard: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', flex: 1, minWidth: '260px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
  actionTitle: { fontSize: '15px', fontWeight: '700', color: '#0c1a33', marginBottom: '8px', fontFamily: "'Space Grotesk', sans-serif" },
  actionDesc: { fontSize: '13px', color: '#4b5563', marginBottom: '16px', lineHeight: '1.5', fontFamily: "'Inter', sans-serif" },
  inputRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' },
  inputLabel: { fontSize: '13px', color: '#4b5563', fontWeight: '500', fontFamily: "'Inter', sans-serif" },
  input: { width: '70px', padding: '8px 10px', border: '1px solid rgba(29,111,219,0.22)', borderRadius: '6px', fontSize: '14px', fontFamily: "'Inter', sans-serif", color: '#0c1a33', outline: 'none' },
  stepArrow: { fontSize: '24px', color: '#9ca3af', paddingTop: '60px' },
  button: { width: '100%', padding: '12px', background: 'linear-gradient(135deg, #1d6fdb, #38bdf8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer', boxShadow: '0 2px 12px rgba(29,111,219,0.25)' },
  commitButton: { width: '100%', padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '14px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  reviewNote: { fontSize: '12px', color: '#dc2626', marginTop: '8px', textAlign: 'center', fontFamily: "'Inter', sans-serif" },
  message: { padding: '12px 16px', borderRadius: '8px', border: '1px solid', fontSize: '14px', marginBottom: '24px', fontFamily: "'Inter', sans-serif" },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(12,26,51,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  dialog: { backgroundColor: 'white', borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '90%', boxShadow: '0 8px 40px rgba(29,111,219,0.15)', border: '1px solid rgba(29,111,219,0.1)' },
  loadingDialog: { backgroundColor: 'white', borderRadius: '16px', padding: '40px', maxWidth: '360px', width: '90%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', boxShadow: '0 8px 40px rgba(29,111,219,0.15)', border: '1px solid rgba(29,111,219,0.1)' },
  spinner: { width: '40px', height: '40px', border: '4px solid rgba(29,111,219,0.15)', borderTopColor: '#1d6fdb', borderRadius: '50%', animation: 'crmbuddy-spin 0.8s linear infinite' },
  loadingTitle: { fontSize: '16px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" },
  loadingText: { fontSize: '13px', color: '#4b5563', lineHeight: '1.5', fontFamily: "'Inter', sans-serif" },
  dialogTitle: { fontSize: '18px', fontWeight: '700', color: '#0c1a33', marginBottom: '12px', fontFamily: "'Space Grotesk', sans-serif" },
  dialogText: { fontSize: '14px', color: '#4b5563', marginBottom: '24px', lineHeight: '1.6', fontFamily: "'Inter', sans-serif" },
  dialogButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  cancelButton: { padding: '10px 20px', backgroundColor: 'transparent', color: '#6b7280', border: '1px solid rgba(29,111,219,0.2)', borderRadius: '8px', fontSize: '14px', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  tableSection: { marginTop: '32px' },
  tableHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  tableTitle: { fontSize: '16px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" },
  reviewedButton: { padding: '8px 16px', background: 'linear-gradient(135deg, #1d6fdb, #38bdf8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  reviewedBadge: { fontSize: '13px', color: '#16a34a', fontWeight: '700', fontFamily: "'Inter', sans-serif" },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#9ca3af', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid rgba(29,111,219,0.08)', fontFamily: "'Inter', sans-serif" },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151', borderBottom: '1px solid rgba(29,111,219,0.06)', fontFamily: "'Inter', sans-serif" },
  rowEven: { backgroundColor: 'white' },
  rowOdd: { backgroundColor: '#f8fafd' },
  badge: { padding: '3px 8px', borderRadius: '20px', color: 'white', fontSize: '11px', fontWeight: '700' },
  customerLink: { color: '#1d6fdb', textDecoration: 'none', fontWeight: '600' },
  cancelPairButton: { padding: '4px 10px', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  mergeButton: { padding: '4px 10px', backgroundColor: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  emptyState: { padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '14px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', fontFamily: "'Inter', sans-serif" }
}

export default AgedRecordRemoval
